import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { api } from "@/lib/api";
import { Search, BedDouble, MapPin, ShieldCheck, MessageSquare, ArrowRight, Heart } from "lucide-react";

export default function LandingPage() {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [q, setQ] = useState("");
  const [stats, setStats] = useState({ total: 0, with_availability: 0, claimed: 0 });

  useEffect(() => { api.get("/facilities/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);

  const go = (e) => { e.preventDefault(); navigate(`/search?q=${encodeURIComponent(q)}`); };

  return (
    <div className="min-h-screen bg-[#F7F6F2]">
      <Header />

      <section className="relative overflow-hidden">
        <div className="max-w-[1600px] mx-auto px-4 md:px-8 grid lg:grid-cols-2 gap-12 items-center py-12 lg:py-20">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#E3F3ED] text-[#1A365D] text-sm font-medium mb-6">
              <BedDouble className="h-4 w-4" /> Real-time senior care bed availability
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-[#1C211F] leading-[1.05]">
              {settings?.hero_title || "Find an open bed for your loved one, faster."}
            </h1>
            <p className="text-lg text-[#4A534E] mt-6 max-w-xl leading-relaxed">
              {settings?.hero_subtitle || `Search ${stats.total || "600+"} licensed facilities across Colorado.`}
            </p>
            <form onSubmit={go} className="mt-8 flex gap-2 max-w-lg">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#5C6660]" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="hero-search"
                  placeholder="Enter a city or county..." className="pl-11 h-14 rounded-xl border-[#E8E6DF] text-base bg-white" />
              </div>
              <Button type="submit" data-testid="hero-search-btn" className="h-14 px-6 bg-[#1A365D] hover:bg-[#12283F] text-white rounded-xl">
                Search <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </form>
            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-6" data-testid="trust-badges">
              <span className="inline-flex items-center gap-1.5 text-sm text-[#2A312D] font-medium"><Heart className="h-4 w-4 text-[#0077B6]" /> Always free for families</span>
              <span className="inline-flex items-center gap-1.5 text-sm text-[#2A312D] font-medium"><ShieldCheck className="h-4 w-4 text-[#0077B6]" /> No referral commissions</span>
              <span className="inline-flex items-center gap-1.5 text-sm text-[#2A312D] font-medium"><MapPin className="h-4 w-4 text-[#0077B6]" /> Every licensed facility</span>
            </div>
            {(settings?.landing_stats_mode || "full") === "full" && (
              <div className="flex gap-8 mt-10" data-testid="landing-stats">
                <Stat n={stats.total} label="Licensed facilities" />
                <Stat n={stats.with_availability} label="With open beds" />
                <Stat n={stats.claimed} label="Verified listings" />
              </div>
            )}
            {(settings?.landing_stats_mode || "full") === "total_only" && (
              <div className="mt-10 flex items-baseline gap-3" data-testid="landing-stats-total">
                <span className="font-mono-data text-4xl sm:text-5xl font-semibold text-[#1A365D]">{stats.total || "600+"}</span>
                <span className="text-[#4A534E] text-lg">licensed facilities across Colorado</span>
              </div>
            )}
          </div>

          <div className="relative animate-fade-up" style={{ animationDelay: ".15s" }}>
            <div className="rounded-3xl overflow-hidden shadow-2xl">
              <img src={settings?.hero_image || "https://images.unsplash.com/photo-1658632302217-984d432b4d38?crop=entropy&cs=srgb&fm=jpg&q=85&w=1100"} alt="Caring senior family" className="w-full h-[460px] object-cover" />
            </div>
            {(settings?.landing_stats_mode || "full") === "full" && (
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-5 border border-[#E8E6DF] hidden sm:block" data-testid="landing-beds-badge">
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 rounded-xl bg-[#E3F3ED] flex items-center justify-center"><BedDouble className="h-6 w-6 text-[#1A365D]" /></div>
                  <div>
                    <div className="font-mono-data text-2xl text-[#1A365D] font-semibold">{stats.with_availability}</div>
                    <div className="text-xs text-[#5C6660] uppercase tracking-wide">facilities with beds today</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="max-w-[1600px] mx-auto px-4 md:px-8 py-16">
        <h2 className="font-heading text-3xl font-medium text-[#1C211F] mb-10">How CareBed helps your family</h2>
        <div className="grid md:grid-cols-3 gap-6">
          <Feature icon={MapPin} title="Search by location" desc="Explore facilities on an interactive map and a side-by-side list, just like browsing homes." />
          <Feature icon={ShieldCheck} title="See live availability" desc="Verified facilities keep open-bed counts current — so you and discharge planners see real openings." />
          <Feature icon={MessageSquare} title="Reach out instantly" desc="Save favorites and send an inquiry with your care needs straight to the facility — no fees, ever." />
        </div>
      </section>

      <section className="max-w-[1600px] mx-auto px-4 md:px-8 pb-20">
        <div className="rounded-3xl bg-white border border-[#E8E6DF] p-10 md:p-14 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="font-heading text-3xl font-medium text-[#1C211F] flex items-center gap-3"><Heart className="h-7 w-7 text-[#0077B6]" /> Care that meets you where you are</h2>
            <p className="text-[#4A534E] mt-2 max-w-2xl">Whether you're planning ahead or facing an urgent hospital discharge, CareBed gives you a clear, calm way to find the right senior care — completely free.</p>
          </div>
          <Link to="/search"><Button className="bg-[#1A365D] hover:bg-[#12283F] text-white rounded-xl h-12 px-6" data-testid="cta-find-care">{settings?.hero_cta || "Find care now"}</Button></Link>
        </div>
        <p className="text-center text-sm text-[#5C6660] mt-8">
          Own or operate a facility? <Link to="/for-facilities" className="text-[#0077B6] font-medium hover:underline" data-testid="for-facilities-link">List your community on CareBed →</Link>
        </p>
      </section>
    </div>
  );
}

const Stat = ({ n, label }) => (
  <div>
    <div className="font-mono-data text-3xl text-[#1A365D] font-semibold">{n ?? "—"}</div>
    <div className="text-xs text-[#5C6660] uppercase tracking-wide mt-1">{label}</div>
  </div>
);

const Feature = ({ icon: Icon, title, desc }) => (
  <div className="bg-white border border-[#E8E6DF] rounded-2xl p-6 hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
    <div className="h-12 w-12 rounded-xl bg-[#E3F3ED] flex items-center justify-center mb-4"><Icon className="h-6 w-6 text-[#1A365D]" /></div>
    <h3 className="font-heading text-xl font-medium text-[#1C211F] mb-2">{title}</h3>
    <p className="text-[#4A534E] leading-relaxed text-sm">{desc}</p>
  </div>
);
